import {
  createNewOrderDraft,
  getCreateOrderDeliveries,
  normalizeAspiratorDraftValue,
  normalizeLoadingPriorityDraftValue,
  normalizeOntarioDraftValue,
  normalizePreloadedPreviousDayDraftValue,
} from "../hooks/useDataManagerOrdersUtils.js";

export const DATA_MANAGER_ORDER_CREATE_DRAFT_STORAGE_KEY = "grain-saas:data-manager:orders:create-draft:v1";

const EMPTY_STORAGE_SNAPSHOT = "";

const TOP_LEVEL_TEXT_FIELDS = [
  "hourly_rate_override",
  "loading_tonne_rate_override",
  "fixed_rate_override",
  "address",
  "building",
  "client_code",
  "client_name",
  "comment",
  "coop",
  "delivery_date",
  "delivery_window_start",
  "grain_code",
  "grain_type",
  "loading_client_name",
  "loading_silo",
  "loading_window_start",
  "order_id",
  "order_date",
  "quantity_tons",
  "work_order_number",
];

const DELIVERY_TEXT_FIELDS = [
  "address",
  "building",
  "comment",
  "delivery_window_start",
  "draft_id",
  "client_name",
  "grain_code",
  "grain_type",
  "loading_silo",
  "loading_window_start",
  "loading_tonne_rate_override",
  "quantity_tons",
  "unloading_tonne_rate_override",
  "work_order_number",
];

const TOP_LEVEL_CONTENT_FIELDS = [
  "hourly_rate_override",
  "loading_tonne_rate_override",
  "fixed_rate_override",
  "address",
  "building",
  "client_code",
  "client_name",
  "comment",
  "coop",
  "delivery_date",
  "delivery_window_start",
  "grain_code",
  "grain_type",
  "is_dompeur",
  "is_preloaded_previous_day",
  "loading_client_name",
  "loading_priority",
  "loading_silo",
  "loading_window_start",
  "quantity_tons",
  "work_order_number",
];

const DELIVERY_CONTENT_FIELDS = [
  "address",
  "aspirator",
  "building",
  "comment",
  "client_name",
  "delivery_priority",
  "delivery_window_start",
  "grain_code",
  "grain_type",
  "loading_priority",
  "loading_silo",
  "loading_window_start",
  "loading_tonne_rate_override",
  "ontario",
  "quantity_tons",
  "unloading_tonne_rate_override",
  "work_order_number",
];

let memoryOrderCreateDraftSnapshot = EMPTY_STORAGE_SNAPSHOT;

function getWindowSessionStorage() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.sessionStorage || null;
  } catch {
    return null;
  }
}

function resolveOrderCreateDraftStorage(storage = null) {
  if (
    storage
    && typeof storage.getItem === "function"
    && typeof storage.setItem === "function"
    && typeof storage.removeItem === "function"
  ) {
    return storage;
  }

  return getWindowSessionStorage();
}

function parseStorageSnapshot(value) {
  if (typeof value !== "string" || !value.trim()) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(value);
    return parsedValue && typeof parsedValue === "object" && !Array.isArray(parsedValue)
      ? parsedValue
      : null;
  } catch {
    return null;
  }
}

function normalizeSourceObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function normalizeTextValue(value) {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  return "";
}

function normalizeCreateDeliveryDraft(delivery) {
  const normalizedDelivery = getCreateOrderDeliveries({ deliveries: [delivery] })[0] || {};
  const nextDelivery = {
    aspirator: normalizeAspiratorDraftValue(normalizedDelivery.aspirator),
    ontario: normalizeOntarioDraftValue(normalizedDelivery.ontario),
    delivery_priority: normalizeLoadingPriorityDraftValue(normalizedDelivery.delivery_priority),
    loading_priority: normalizeLoadingPriorityDraftValue(normalizedDelivery.loading_priority),
  };

  DELIVERY_TEXT_FIELDS.forEach((field) => {
    nextDelivery[field] = normalizeTextValue(normalizedDelivery[field]);
  });

  return nextDelivery;
}

export function normalizeDataManagerOrderCreateDraft(draft = null) {
  const fallbackDraft = createNewOrderDraft();
  const sourceDraft = normalizeSourceObject(draft);
  const nextDraft = {
    ...fallbackDraft,
    aspirator: normalizeAspiratorDraftValue(sourceDraft.aspirator ?? fallbackDraft.aspirator),
    ontario: normalizeOntarioDraftValue(sourceDraft.ontario ?? fallbackDraft.ontario),
    delivery_priority: normalizeLoadingPriorityDraftValue(sourceDraft.delivery_priority ?? fallbackDraft.delivery_priority),
    is_dompeur: normalizePreloadedPreviousDayDraftValue(sourceDraft.is_dompeur ?? fallbackDraft.is_dompeur),
    is_preloaded_previous_day: normalizePreloadedPreviousDayDraftValue(
      sourceDraft.is_preloaded_previous_day ?? fallbackDraft.is_preloaded_previous_day,
    ),
    loading_priority: normalizeLoadingPriorityDraftValue(sourceDraft.loading_priority ?? fallbackDraft.loading_priority),
    deliveries: getCreateOrderDeliveries(sourceDraft).map((delivery) => normalizeCreateDeliveryDraft(delivery)),
  };

  TOP_LEVEL_TEXT_FIELDS.forEach((field) => {
    nextDraft[field] = normalizeTextValue(sourceDraft[field] ?? fallbackDraft[field]);
  });

  return nextDraft;
}

export function normalizeDataManagerOrderCreateDraftState(value = null) {
  const sourceState = normalizeSourceObject(value);

  return {
    draft: normalizeDataManagerOrderCreateDraft(sourceState.draft),
    isCreatingOrder: sourceState.isCreatingOrder === true,
  };
}

export function hasDataManagerOrderCreateDraftContent(draft = null) {
  const normalizedDraft = normalizeDataManagerOrderCreateDraft(draft);
  const defaultDraft = createNewOrderDraft();
  const defaultDelivery = getCreateOrderDeliveries(defaultDraft)[0] || {};
  const normalizedDeliveries = getCreateOrderDeliveries(normalizedDraft);

  if (
    TOP_LEVEL_CONTENT_FIELDS.some((field) => normalizeTextValue(normalizedDraft[field]) !== normalizeTextValue(defaultDraft[field]))
  ) {
    return true;
  }

  if (normalizedDeliveries.length !== 1) {
    return true;
  }

  return normalizedDeliveries.some((delivery) => (
    DELIVERY_CONTENT_FIELDS.some((field) => normalizeTextValue(delivery[field]) !== normalizeTextValue(defaultDelivery[field]))
  ));
}

export function readDataManagerOrderCreateDraftState(storage = null) {
  const resolvedStorage = resolveOrderCreateDraftStorage(storage);
  let rawSnapshot = EMPTY_STORAGE_SNAPSHOT;

  if (resolvedStorage) {
    try {
      rawSnapshot = resolvedStorage.getItem(DATA_MANAGER_ORDER_CREATE_DRAFT_STORAGE_KEY) || EMPTY_STORAGE_SNAPSHOT;
    } catch {
      rawSnapshot = memoryOrderCreateDraftSnapshot;
    }
  } else {
    rawSnapshot = memoryOrderCreateDraftSnapshot;
  }

  const parsedSnapshot = parseStorageSnapshot(rawSnapshot);
  return parsedSnapshot ? normalizeDataManagerOrderCreateDraftState(parsedSnapshot) : null;
}

export function persistDataManagerOrderCreateDraftState(state = null, storage = null) {
  const normalizedState = normalizeDataManagerOrderCreateDraftState(state);
  const shouldPersist = normalizedState.isCreatingOrder || hasDataManagerOrderCreateDraftContent(normalizedState.draft);

  if (!shouldPersist) {
    clearDataManagerOrderCreateDraftState(storage);
    return normalizedState;
  }

  const nextSnapshot = JSON.stringify(normalizedState);
  const resolvedStorage = resolveOrderCreateDraftStorage(storage);
  memoryOrderCreateDraftSnapshot = nextSnapshot;

  if (resolvedStorage) {
    try {
      resolvedStorage.setItem(DATA_MANAGER_ORDER_CREATE_DRAFT_STORAGE_KEY, nextSnapshot);
    } catch {
      return normalizedState;
    }
  }

  return normalizedState;
}

export function clearDataManagerOrderCreateDraftState(storage = null) {
  const resolvedStorage = resolveOrderCreateDraftStorage(storage);
  memoryOrderCreateDraftSnapshot = EMPTY_STORAGE_SNAPSHOT;

  if (!resolvedStorage) {
    return;
  }

  try {
    resolvedStorage.removeItem(DATA_MANAGER_ORDER_CREATE_DRAFT_STORAGE_KEY);
  } catch {
    // Storage is best-effort only.
  }
}
